/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { orderBy } from 'lodash';
import { getDate } from '../../utils/getDate';
import { resetLogAnalyzer } from '../../features/LogAnalyzer/logAnalyzerSlice';
import { openSnackbar } from '../../features/SnackBar/snackBarSlice';
import { getLogAnalyzer, getLogAnalyzerDetails } from '../../features/LogAnalyzer/logAnalyzerAction';
import { getDistinctAccountsList, getCustomEntityList } from '../../features';

const currentDate = getDate();
const defaultFilter = '- all -';
const defaultFilterValue = {
  account: null,
  customEntityType: [],
  customEntity: {},
  timeFrame: '',
};

function useLogAnalyzer() {
  const dispatch = useDispatch();

  const accountListData = useSelector((state) => state.distinctAccountsList?.data);
  const customEntityData = useSelector((state) => state.customEntityList);
  const logAnalyzerData = useSelector((state) => state.logAnalyzer);

  // State to store API data ----->
  const [accountList, setAccountList] = useState([]);
  const [customEntityList, setCustomEntityList] = useState([]);
  const [logAnalyzerInfo, setLogAnalyzerInfo] = useState({
    loading: false,
    error: null,
    data: {},
  });

  useEffect(() => {
    dispatch(getDistinctAccountsList());
    dispatch(getCustomEntityList({ type: '' }));
  }, []);

  //   rest of the hook ----->
  const [selectedFilters, setSelectedFilters] = useState(defaultFilterValue);
  const [customEntityTpe, setCustomEntityTpe] = useState('');

  const handleFilterChange = (name, value) => {
    setSelectedFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    const { timeFrame, account, customEntity } = selectedFilters;

    if (timeFrame && Object.keys(customEntity).length) {
      dispatch(getLogAnalyzer({
        view: timeFrame,
        customEntityObjects: customEntity,
        ...(account?.value && { orgId: account.value }),
      }));
    } else {
      dispatch(openSnackbar({ open: true, message: 'Please select all the filters!', type: 'error' }));
    }
  };

  const handleExpandEntity = (type) => {
    const { timeFrame, account, customEntity } = selectedFilters;
    setCustomEntityTpe(type);

    if (!timeFrame || !customEntity[type]) {
      dispatch(openSnackbar({ open: true, message: 'Please select all the filters!', type: 'error' }));
      return;
    }

    const payload = {
      view: timeFrame,
      customEntityObjects: { [type]: customEntity[type] },
      ...(account?.value && account.value !== defaultFilter && { orgId: account.value }),
    };

    dispatch(getLogAnalyzerDetails(payload));
  };

  const handleResetFilters = () => {
    setSelectedFilters(defaultFilterValue);
    dispatch(resetLogAnalyzer());
  };

  // State setter functions ----->
  const setCustomEntityListData = (selectedCustomEntityType) => {
    if (!selectedCustomEntityType.length) {
      setCustomEntityList([]);
      return;
    }

    const filteredCustomEntityList = [];
    if (customEntityData?.customEntityList?.length) {
      customEntityData.customEntityList.forEach((entity) => {
        const { custom_entity_id, custom_entity_label, custom_entity_type } = entity;
        const isApex = selectedCustomEntityType.includes('ApexExecution') && (custom_entity_type === 'ApexClass' || custom_entity_type === 'ApexTrigger');
        if (selectedCustomEntityType.includes(custom_entity_type) || isApex) {
          const object = {
            id: custom_entity_id,
            name: custom_entity_label,
            value: custom_entity_id,
            type: custom_entity_type,
          };
          filteredCustomEntityList.push(object);
        }
      });
    }
    setCustomEntityList(filteredCustomEntityList);
  };

  const setLogAnalyzerData = () => {
    if (logAnalyzerData) {
      setLogAnalyzerInfo({
        loading: logAnalyzerData.loading,
        error: logAnalyzerData.error,
        data: logAnalyzerData.data,
      });
    }
  };

  const setAccountListData = () => {
    const filteredAccount = [];
    const accountsWithoutName = [];
    if (accountListData?.length) {
      accountListData.forEach((acc) => {
        const isActive = acc.status === 'Active';
        const isNeverExpire = acc.renewalDate === null;
        const isRenewalDateValid = acc.renewalDate && getDate(acc.renewalDate) >= currentDate.setHours(0, 0, 0, 0);

        if (isActive && (isRenewalDateValid || isNeverExpire)) {
          const object = {};
          object.value = acc.accountId;
          object.name = acc.accountName;
          object.id = acc.accountId;
          if (!acc.accountName) {
            accountsWithoutName.push(object);
          } else {
            filteredAccount.push(object);
          }
        }
      });
    }
    const sortedData = orderBy(filteredAccount, ['name'], ['asc']);
    setAccountList([...sortedData, ...accountsWithoutName]);
  };

  useEffect(() => {
    if (selectedFilters?.customEntityType) {
      setCustomEntityListData(selectedFilters.customEntityType);
    }
  }, [customEntityData.customEntityList, selectedFilters.customEntityType]);

  useEffect(() => {
    setAccountListData();
  }, [accountListData]);

  useEffect(() => {
    setLogAnalyzerData();
  }, [logAnalyzerData.data]);

  useEffect(() => {
    return () => {
      dispatch(resetLogAnalyzer());
    };
  }, []);

  return {
    accountList,
    customEntityList,
    logAnalyzerInfo,
    selectedFilters,
    customEntityTpe,
    handleFilterChange,
    handleApplyFilters,
    handleResetFilters,
    handleExpandEntity,
  };
}

export default useLogAnalyzer;
